import type { SolanaTree } from "@stealthos/sdk";

export function fail(message: string): never {
  throw new Error(`Solana JoinSplit V2 wallet: ${message}`);
}

export function assertNotAborted(
  signal: AbortSignal | undefined,
  stage: string,
): void {
  if (signal?.aborted) fail(`operation was cancelled ${stage}`);
}

export function decimalScalar(value: bigint | number): `${bigint}` {
  return BigInt(value).toString() as `${bigint}`;
}

/**
 * The tree's latest root. The program records a root once per instruction
 * and mirrors the latest one into `final_root`, for retired and live trees
 * alike, so at rest this is always the root the next proof must open.
 */
export function solanaTreeCurrentRoot(tree: SolanaTree): string {
  if (tree.rootCount === 0n) fail("tree account has no recorded root");
  return tree.finalRoot.toLowerCase();
}

export function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new Error("Solana JoinSplit V2 wallet: operation was cancelled"));
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new Error("Solana JoinSplit V2 wallet: operation was cancelled"));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

export function requireSafeIndex(
  value: number | bigint,
  label: string,
): number {
  const asNumber = Number(value);
  if (
    typeof value === "bigint"
      ? value < 0n || value > BigInt(Number.MAX_SAFE_INTEGER)
      : !Number.isSafeInteger(value) || value < 0
  ) {
    fail(`${label} is not a canonical index`);
  }
  return asNumber;
}

export function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) return false;
  }
  return true;
}
